import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import { createTableSlice } from '../../../common/datagrid';
import {
    CreateProductCategoryDto,
    CreateProductDto,
    CreateProductGroupDto,
    ErrorResponse,
    ProductCategoryDto,
    ProductDto,
    ProductGroupDto,
    TableType
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';
import { fetchWarehouses } from '../../stock/redux/stockSlices';

const namespaces = {
    products: 'products',
    productsIsLoading: 'productsIsLoading',
    productCategories: 'productCategories',
    categorySearchString: 'categorySearchString',
    productGroups: 'productGroups',
    selectedWarehouse: 'selectedWarehouse',
    ui: 'ui'
};

const productEntity = new schema.Entity<ProductDto>('products', undefined, {
    idAttribute: 'productId'
});

export const fetchProducts = createAsyncAction<void, ProductDto[], ErrorResponse>(
    namespaces.products,
    'fetchProducts'
);

type UpdateProduct = { productId: number; dto: ProductDto };
export const updateProduct = createAsyncAction<UpdateProduct, UpdateProduct>(
    namespaces.products,
    'updateProduct'
);

export const createProduct = createAsyncAction<CreateProductDto, ProductDto>(
    namespaces.products,
    'createProduct'
);

export const deleteProduct = createAsyncAction<number, number>(namespaces.products, 'deleteProduct');

const productsSlice = createSlice({
    name: namespaces.products,
    initialState: {} as { [productId: string]: ProductDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(updateProduct.request, (state, action) => {
                state[action.payload.productId].hidePrices = action.payload.dto.hidePrices;
            })
            .addCase(fetchProducts.success, (state, action) => {
                const normalized = normalize(action.payload, [productEntity]);
                return normalized.entities.products ?? {};
            })
            .addCase(updateProduct.success, (state, { payload }) => {
                const { dto, productId } = payload;
                const product = state[productId];
                if (product != null) {
                    product.hidePrices = dto.hidePrices;
                }
            })
            .addCase(createProduct.success, (state, action) => {
                state[action.payload.productId] = action.payload;
            })
            .addCase(deleteProduct.success, (state, action) => {
                delete state[action.payload];
            })
});

const isLoadingSlice = createSlice({
    name: namespaces.productsIsLoading,
    initialState: true,
    reducers: {},
    extraReducers: builders =>
        builders
            .addCase(fetchProducts.request, () => true)
            .addCase(fetchProducts.success, () => false)
            .addCase(fetchProducts.failure, () => false)
});

const initialProductTableColumns = [
    'hidePrices',
    'productId',
    'productName',
    'productCategory1Id',
    'productCategory2Id',
    'productGroupId',
    'sortOrder',
    'netWeight',
    'grossWeight',
    'conversionFactor',
    'countryOfOrigin',
    'priceUnit',
    'vat',
    'customAttribute1',
    'boxesPerPallet',
    'palletType'
].map(key => ({
    key: key,
    hide: key === 'customAttribute1',
    width: undefined
}));

const productTableSlice = createTableSlice('productTable', {
    table: TableType.Product,
    filters: {
        hidePrices: '',
        productId: '',
        productName: '',
        productCategory1Id: '',
        productCategory2Id: '',
        productGroupId: '',
        sortOrder: '',
        netWeight: '',
        grossWeight: '',
        conversionFactor: '',
        countryOfOrigin: '',
        priceUnit: '',
        vat: '',
        customAttribute1: '',
        boxesPerPallet: '',
        palletType: ''
    },
    sorting: {
        column: 'productId',
        direction: 'asc'
    },
    columns: initialProductTableColumns,
    showTooltips: true
});

const productCategoryEntity = new schema.Entity<ProductCategoryDto>('productCategories', undefined, {
    idAttribute: 'productCategoryId'
});

export const fetchProductCategories = createAsyncAction<void, ProductCategoryDto[]>(
    namespaces.productCategories,
    'fetchProductCategories'
);

export const createProductCategory = createAsyncAction<CreateProductCategoryDto, ProductCategoryDto>(
    namespaces.productCategories,
    'createProductCategory'
);

export const updateProductCategory = createAsyncAction<ProductCategoryDto, ProductCategoryDto>(
    namespaces.productCategories,
    'updateProductCategory'
);
export const deleteProductCategory = createAsyncAction<number, number>(
    namespaces.productCategories,
    'deleteProductCategory'
);

export const createProductGroup = createAsyncAction<CreateProductGroupDto, ProductGroupDto>(
    namespaces.productGroups,
    'createProductGroup'
);
export const deleteProductGroup = createAsyncAction<number, number>(
    namespaces.productGroups,
    'deleteProductGroup'
);
export const updateProductGroup = createAsyncAction<ProductGroupDto, ProductGroupDto>(
    namespaces.productGroups,
    'updateProductGroup'
);

const productCategoriesSlice = createSlice({
    name: namespaces.productCategories,
    initialState: {} as { [productCategoryId: string]: ProductCategoryDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchProductCategories.success, (_, action) => {
                const normalized = normalize(action.payload, [productCategoryEntity]);
                return normalized.entities.productCategories ?? {};
            })
            .addCase(createProductCategory.success, (state, action) => {
                state[action.payload.productCategoryId] = action.payload;
            })
            .addCase(updateProductCategory.success, (state, action) => {
                state[action.payload.productCategoryId] = action.payload;
            })
            .addCase(deleteProductCategory.success, (state, action) => {
                delete state[action.payload];
            })
});

const categorySearchStringSlice = createSlice({
    name: namespaces.categorySearchString,
    initialState: '',
    reducers: {
        searchProductCategory: (state, action: PayloadAction<string>) => action.payload
    }
});
export const { searchProductCategory } = categorySearchStringSlice.actions;

const productGroupEntity = new schema.Entity<ProductGroupDto>('productGroups', undefined, {
    idAttribute: 'productGroupId'
});

export const fetchProductGroups = createAsyncAction<void, ProductGroupDto[]>(
    namespaces.productGroups,
    'fetchProductGroups'
);

const productGroupsSlice = createSlice({
    name: namespaces.productGroups,
    initialState: {} as { [productGroupId: string]: ProductGroupDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchProductGroups.success, (_, action) => {
                const normalized = normalize(action.payload, [productGroupEntity]);
                return normalized.entities.productGroups ?? {};
            })
            .addCase(createProductGroup.success, (state, action) => {
                state[action.payload.productGroupId] = action.payload;
            })
            .addCase(updateProductGroup.success, (state, action) => {
                state[action.payload.productGroupId] = action.payload;
            })
            .addCase(deleteProductGroup.success, (state, action) => {
                delete state[action.payload];
            })
});

const selectedWarehouseSlice = createSlice({
    name: namespaces.selectedWarehouse,
    initialState: null as number | null,
    reducers: {
        warehouseSelected: (state, action: PayloadAction<number>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(
            fetchWarehouses.success,
            (_, { payload }) => payload.find(x => x.isPrimaryWarehouse)?.warehouseId
        )
});

export const { warehouseSelected } = selectedWarehouseSlice.actions;

export const { productTableActions } = productTableSlice;

const selectedProductSlice = createSlice({
    name: 'selectedProduct',
    initialState: null as number | null,
    reducers: { productSelected: (state, action: PayloadAction<number>) => action.payload }
});

export const { productSelected } = selectedProductSlice.actions;

export const uiSlice = createSlice({
    name: namespaces.ui,
    initialState: { createProductDialogVisible: false },
    reducers: {
        showCreateProductDialog: (state, action: PayloadAction<boolean>) => ({
            ...state,
            createProductDialogVisible: action.payload
        })
    }
});

export const { showCreateProductDialog } = uiSlice.actions;
export default combineReducers({
    products: productsSlice.reducer,
    isLoading: isLoadingSlice.reducer,
    productCategories: productCategoriesSlice.reducer,
    productGroups: productGroupsSlice.reducer,
    categorySearchString: categorySearchStringSlice.reducer,
    table: productTableSlice.reducer,
    selectedWarehouse: selectedWarehouseSlice.reducer,
    ui: uiSlice.reducer,
    selectedProduct: selectedProductSlice.reducer
});
